import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import TableToolTip from "../../components/ToolTips/TableToolTip";
import IconComponent from "../../components/Icons/IconComponent";
import * as BrokerStyles from "../Broker/Styles.js";
import {
  buildTableRowsPerPageOptions,
  TABLE_COMPACT_HEADER_PADDING,
  TABLE_HEADER_FONT_SIZE,
  TABLE_INITIAL_ROWS_PER_PAGE,
} from "../Table/constants.js";

import {
  PageRoot,
  MainCard__body,
  TitleWrap,
  Toolbar,
  SearchBox,
  FiltersWrap,
  FiltersButton,
  HeaderCell,
  BodyCell,
  Footer,
  CardSplit,
  TablePane,
  FiltersPane,
  FiltersPaneHeader,
  FiltersChipsRow,
  FiltersForm,
  FiltersRow2,
  FiltersActions,
} from "./Styles.js";

import TradingGroupsService from "../../Endpoints/TradingGroups";

const emptyFilters = {
  server: "",
  currency: "",
  markup: "",
  rebate: "",
  symbolSecurity: "",
};

const TRADING_GROUP_COLUMNS = [
  { field: "group", label: "Group", minWidth: 200, width: 230 },
  { field: "server", label: "Server", minWidth: 130, width: 150 },
  { field: "currency", label: "Currency", minWidth: 110, width: 120 },
  { field: "leverage", label: "Leverage", minWidth: 110, width: 120 },
  { field: "mcso", label: "MCSO", minWidth: 160, width: 190 },
];

const initialTradingGroupColumnWidths = TRADING_GROUP_COLUMNS.reduce(
  (widths, column) => {
    widths[column.field] = column.width;
    return widths;
  },
  {}
);
const tradingGroupsHeaderCellSx = {
  fontSize: TABLE_HEADER_FONT_SIZE,
  fontWeight: 700,
  color: "rgba(0,0,0,0.70)",
  backgroundColor: "#F9F9F9",
  borderBottom: 0,
  height: 40,
  minHeight: 40,
  maxHeight: 40,
  paddingTop: 0,
  paddingBottom: 0,
};

function flattenTradingGroups(apiPayload) {
  const groups = apiPayload?.groups;
  if (!Array.isArray(groups)) return [];

  return groups.map((group, index) => ({
    id: group?.id ?? index + 1,
    group: group?.group ?? "",
    server: group?.server ?? "",
    currency: group?.currency ?? "",
    leverage: group?.leverage ?? "",
    mcso: group?.mcso ?? "",
    groupSplit: Array.isArray(group?.group_split) ? group.group_split : [],
  }));
}

function TradingGroups() {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  // custom pagination (0-based)
  const ROWS_OPTIONS = buildTableRowsPerPageOptions([5, 7, 10, 25, 50, 100]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(TABLE_INITIAL_ROWS_PER_PAGE);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [focusedGroupId, setFocusedGroupId] = useState(null);

  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  const [rowsRaw, setRowsRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [columnWidths, setColumnWidths] = useState(
    initialTradingGroupColumnWidths
  );

  // dynamic options
  const serverOptions = useMemo(() => {
    const set = new Set(rowsRaw.map((r) => r.server).filter(Boolean));
    return Array.from(set);
  }, [rowsRaw]);

  const currencyOptions = useMemo(() => {
    const set = new Set(rowsRaw.map((r) => r.currency).filter(Boolean));
    return Array.from(set);
  }, [rowsRaw]);

  // functional search (debounce)
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(search.trim());
      setPage(0);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  // Apply button (optional)
  const handleApply = () => {
    setAppliedSearch(search.trim());
    setPage(0);
  };

  const openFilters = () => {
    setDraftFilters(appliedFilters);
    setFocusedGroupId(null);
    setFiltersOpen(true);
  };

  const closeFilters = () => setFiltersOpen(false);

  const clearFilters = () => {
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setAppliedSearch("");
    setSearch("");
    setPage(0);
  };

  // Apply filters (now also closes the panel; auto-apply already applies them)
  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setAppliedSearch(search.trim());
    setPage(0);
    setFiltersOpen(false);
  };

  // AUTO-APPLY: every form change is applied (with debounce) while the panel is open
  useEffect(() => {
    if (!filtersOpen) return;

    const t = setTimeout(() => {
      setAppliedFilters(draftFilters);
      setPage(0);
    }, 250);

    return () => clearTimeout(t);
  }, [draftFilters, filtersOpen]);

  const removeDraftChip = (key) => {
    setDraftFilters((prev) => ({ ...prev, [key]: "" }));
  };

  const removeAppliedChip = (key) => {
    setAppliedFilters((prev) => ({ ...prev, [key]: "" }));
    setPage(0);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const res = await TradingGroupsService.getTradingGroups();
        if (!res) {
          setRowsRaw([]);
          setErrorMsg("Error loading trading groups");
          return;
        }
        const payload = res?.data?.data ?? res?.data ?? null;
        const flat = flattenTradingGroups(payload);
        setRowsRaw(flat);
      } catch (e) {
        setRowsRaw([]);
        setErrorMsg(e?.message || "Error loading trading groups");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredRows = useMemo(() => {
    const s = appliedSearch.toLowerCase();

    return rowsRaw.filter((row) => {
      const matchesSearch =
        !s ||
        String(row.group).toLowerCase().includes(s) ||
        String(row.server).toLowerCase().includes(s) ||
        String(row.currency).toLowerCase().includes(s) ||
        row.groupSplit.some((split) =>
          String(split?.symbol_or_security ?? "").toLowerCase().includes(s)
        );

      const matchesServer =
        !appliedFilters.server || row.server === appliedFilters.server;

      const matchesCurrency =
        !appliedFilters.currency || row.currency === appliedFilters.currency;

      const matchesMarkup = (() => {
        if (!appliedFilters.markup) return true;
        const want = String(appliedFilters.markup).trim();
        return row.groupSplit.some((split) => String(split?.markup ?? "") === want);
      })();

      const matchesRebate = (() => {
        if (!appliedFilters.rebate) return true;
        const want = String(appliedFilters.rebate).trim();
        return row.groupSplit.some((split) => String(split?.rebate ?? "") === want);
      })();

      const matchesSymbol =
        !appliedFilters.symbolSecurity ||
        row.groupSplit.some((split) =>
          String(split?.symbol_or_security ?? "")
            .toLowerCase()
            .includes(String(appliedFilters.symbolSecurity).toLowerCase())
        );

      return (
        matchesSearch &&
        matchesServer &&
        matchesCurrency &&
        matchesMarkup &&
        matchesRebate &&
        matchesSymbol
      );
    });
  }, [rowsRaw, appliedSearch, appliedFilters]);

  const focusedGroup = useMemo(
    () => rowsRaw.find((row) => row.id === focusedGroupId) ?? null,
    [rowsRaw, focusedGroupId]
  );

  const handleGroupClick = (id) => {
    setFiltersOpen(false);
    setFocusedGroupId((currentId) => (currentId === id ? null : id));
  };

  const pageCount = useMemo(() => {
    const total = filteredRows.length;
    return Math.max(1, Math.ceil(total / rowsPerPage));
  }, [filteredRows.length, rowsPerPage]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  // clamp page
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  // reset page when the size or total changes
  useEffect(() => {
    setPage(0);
  }, [rowsPerPage, filteredRows.length]);

  const pageItems = useMemo(() => {
    const current = page + 1;
    const last = pageCount;

    if (last <= 4) return Array.from({ length: last }, (_, i) => i + 1);

    if (current <= 2) return [1, 2, 3, 4, "…"];
    if (current >= last - 1) return ["…", last - 3, last - 2, last - 1, last];

    return ["…", current - 1, current, current + 1, "…"];
  }, [page, pageCount]);

  const draftChips = useMemo(() => {
    const chips = [];
    if (draftFilters.server)
      chips.push({ key: "server", label: `#${draftFilters.server}` });
    if (draftFilters.currency)
      chips.push({ key: "currency", label: `#${draftFilters.currency}` });
    return chips;
  }, [draftFilters]);

  const appliedChips = useMemo(() => {
    const chips = [];
    if (appliedFilters.server)
      chips.push({ key: "server", label: `#${appliedFilters.server}` });
    if (appliedFilters.currency)
      chips.push({ key: "currency", label: `#${appliedFilters.currency}` });
    if (appliedFilters.markup)
      chips.push({ key: "markup", label: `#Markup ${appliedFilters.markup}` });
    if (appliedFilters.rebate)
      chips.push({ key: "rebate", label: `#Rebate ${appliedFilters.rebate}` });
    if (appliedFilters.symbolSecurity)
      chips.push({
        key: "symbolSecurity",
        label: `#${appliedFilters.symbolSecurity}`,
      });
    return chips;
  }, [appliedFilters]);

  const appliedFiltersCount = useMemo(() => {
    return Object.values(appliedFilters).filter(
      (v) => String(v || "").trim() !== ""
    ).length;
  }, [appliedFilters]);

  const tableMinWidth = useMemo(() => {
    return TRADING_GROUP_COLUMNS.reduce(
      (total, column) => total + (columnWidths[column.field] || column.width),
      0
    );
  }, [columnWidths]);

  const columnCellSx = useMemo(() => {
    return TRADING_GROUP_COLUMNS.reduce((styles, column) => {
      const width = columnWidths[column.field] || column.width;

      styles[column.field] = {
        width,
        minWidth: column.minWidth,
        maxWidth: width,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      };

      return styles;
    }, {});
  }, [columnWidths]);

  const handleColumnResizeStart = useCallback(
    (event, column) => {
      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = columnWidths[column.field] || column.width;
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handlePointerMove = (moveEvent) => {
        const nextWidth = Math.max(
          column.minWidth,
          startWidth + moveEvent.clientX - startX
        );

        setColumnWidths((prev) => ({
          ...prev,
          [column.field]: nextWidth,
        }));
      };

      const handlePointerUp = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    },
    [columnWidths]
  );

  const handleColumnResizeKeyDown = useCallback((event, column) => {
    const direction =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;

    if (!direction) return;

    event.preventDefault();
    event.stopPropagation();

    setColumnWidths((prev) => {
      const currentWidth = prev[column.field] || column.width;

      return {
        ...prev,
        [column.field]: Math.max(column.minWidth, currentWidth + direction * 16),
      };
    });
  }, []);

  return (
    <PageRoot>
      <TitleWrap sx={{ mb: 4 }}>
        <Typography
          sx={{
            fontFamily: "Plus Jakarta Sans",
            fontSize: "26px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "140%",
          }}
        >
          Trading groups
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View the groups, their characteristics, and configurations.
        </Typography>
      </TitleWrap>

      <MainCard__body elevation={2}>
        <Toolbar>
          <SearchBox>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApply();
                if (e.key === "Escape") setSearch("");
              }}
              sx={{
                ...BrokerStyles.InputStyles,
                width: 320,
                "& .MuiOutlinedInput-root": { borderRadius: "10px" },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </SearchBox>

          <FiltersWrap>
            {appliedChips.length > 0 && (
              <Button
                variant="text"
                onClick={clearFilters}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  height: "40px",
                  padding: "12px",
                }}
                startIcon={<IconComponent name="refresh" />}
              >
                Clear filters
              </Button>
            )}

            <FiltersButton
              variant="contained"
              startIcon={<IconComponent icon="filter_alt" />}
              onClick={openFilters}
              sx={{
                backgroundColor: "#EB0000",
                height: "40px",
                padding: "12px",
                "&:hover": {
                  backgroundColor: "#D50000",
                },
              }}
            >
              Filters{appliedFiltersCount ? ` (${appliedFiltersCount})` : ""}
            </FiltersButton>
          </FiltersWrap>
        </Toolbar>

        {!!errorMsg && (
          <Box sx={{ px: 2, pt: 1 }}>
            <Alert severity="error">{errorMsg}</Alert>
          </Box>
        )}

        <Box
          sx={{
            width: "100%",
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <CardSplit>
          {/* ================= LEFT: TABLE ================= */}
          <TablePane
            sx={
              focusedGroup
                ? {
                    flex: { xs: "1 1 auto", md: "0 0 60%" },
                    width: { xs: "100%", md: "60%" },
                  }
                : undefined
            }
          >
            <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
              <Table sx={{ minWidth: tableMinWidth, tableLayout: "fixed" }}>
                <colgroup>
                  {TRADING_GROUP_COLUMNS.map((column) => (
                    <col
                      key={column.field}
                      style={{
                        width: `${columnWidths[column.field] || column.width}px`,
                      }}
                    />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow>
                    {TRADING_GROUP_COLUMNS.map((column) => (
                      <HeaderCell
                        key={column.field}
                        sx={{
                          ...columnCellSx[column.field],
                          ...tradingGroupsHeaderCellSx,
                          position: "relative",
                          pr: TABLE_COMPACT_HEADER_PADDING,
                          userSelect: "none",
                        }}
                      >
                        <Box
                          component="span"
                          title={column.label}
                          sx={{
                            display: "block",
                            lineHeight: "18px",
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {column.field === "group" ? (
                            <>
                              Group <TableToolTip title="User group name" />
                            </>
                          ) : column.field === "mcso" ? (
                            <>
                              MCSO{" "}
                              <TableToolTip title="Margin Call / Stop Out configuration" />
                            </>
                          ) : column.field === "markup" ? (
                            <>
                              Markup{" "}
                              <TableToolTip title="Markup configuration for this group" />
                            </>
                          ) : column.field === "rebate" ? (
                            <>
                              Rebate{" "}
                              <TableToolTip title="Rebate configuration for this group" />
                            </>
                          ) : (
                            column.label
                          )}
                        </Box>
                        <Box
                          component="span"
                          role="separator"
                          aria-label={`Resize ${column.label} column`}
                          aria-orientation="vertical"
                          aria-valuemin={column.minWidth}
                          aria-valuenow={columnWidths[column.field] || column.width}
                          tabIndex={0}
                          onPointerDown={(event) =>
                            handleColumnResizeStart(event, column)
                          }
                          onKeyDown={(event) =>
                            handleColumnResizeKeyDown(event, column)
                          }
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: "12px",
                            height: "100%",
                            cursor: "col-resize",
                            touchAction: "none",
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              top: "22%",
                              bottom: "22%",
                              left: "50%",
                              width: "2px",
                              transform: "translateX(-50%)",
                              borderRadius: "2px",
                              backgroundColor: "rgba(21, 32, 42, 0.22)",
                            },
                            "&:hover::after, &:focus-visible::after": {
                              backgroundColor: "#15202A",
                            },
                            "&:focus-visible": {
                              outline: "2px solid #15202A",
                              outlineOffset: "-2px",
                            },
                          }}
                        />
                      </HeaderCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <BodyCell
                        colSpan={TRADING_GROUP_COLUMNS.length}
                        sx={{ py: 5, textAlign: "center" }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <CircularProgress size={18} />
                          <Typography color="text.secondary" fontWeight={700}>
                            Loading...
                          </Typography>
                        </Box>
                      </BodyCell>
                    </TableRow>
                  ) : (
                    <>
                      {pagedRows.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={() => handleGroupClick(row.id)}
                          sx={{
                            cursor: "pointer",
                            backgroundColor:
                              focusedGroupId === row.id ? "#E8F1FF" : "#FFFFFF",
                          }}
                        >
                          <BodyCell title={row.group} sx={columnCellSx.group}>
                            {row.group}
                          </BodyCell>
                          <BodyCell title={row.server} sx={columnCellSx.server}>
                            {row.server}
                          </BodyCell>
                          <BodyCell title={row.currency} sx={columnCellSx.currency}>
                            {row.currency}
                          </BodyCell>
                          <BodyCell title={row.leverage} sx={columnCellSx.leverage}>
                            {row.leverage}
                          </BodyCell>
                          <BodyCell title={row.mcso} sx={columnCellSx.mcso}>
                            {row.mcso}
                          </BodyCell>
                        </TableRow>
                      ))}

                      {pagedRows.length === 0 && (
                        <TableRow>
                          <BodyCell
                            colSpan={TRADING_GROUP_COLUMNS.length}
                            sx={{ py: 5, textAlign: "center" }}
                          >
                            <Typography color="text.secondary" fontWeight={600}>
                              No results found.
                            </Typography>
                          </BodyCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Footer>
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                Total:&nbsp;<b style={{ color: "#111827" }}>{filteredRows.length}</b>
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                  Pages:&nbsp;<b style={{ color: "#111827" }}>{pageCount}</b>
                </Typography>

                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>Rows:</Typography>

                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  sx={{
                    height: 32,
                    minWidth: 70,
                    borderRadius: 2,
                    ".MuiSelect-select": { py: 0.5 },
                  }}
                  MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 0.5 } } }}
                  disabled={loading}
                >
                  {ROWS_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Button
                    disabled={loading || page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    sx={{
                      minWidth: 28,
                      height: 28,
                      borderRadius: 2,
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E5E7EB",
                      color: loading || page === 0 ? "#9CA3AF" : "#374151",
                      boxShadow: "none",
                      padding: 0,
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                        borderColor: "#D1D5DB",
                        boxShadow: "none",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#FFFFFF",
                        borderColor: "#E5E7EB",
                        color: "#9CA3AF",
                        opacity: 1,
                      },
                    }}
                  >
                    <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                  </Button>

                  {pageItems.map((it, idx) => {
                    if (it === "…") {
                      return (
                        <Typography
                          key={`dots-${idx}`}
                          sx={{ fontSize: 12, color: "#9CA3AF", px: 0.5 }}
                        >
                          …
                        </Typography>
                      );
                    }

                    const pageNumber = it;
                    const isActive = pageNumber === page + 1;

                    return (
                      <Button
                        key={`p-${pageNumber}`}
                        disabled={loading}
                        onClick={() => setPage(pageNumber - 1)}
                        sx={{
                          minWidth: 28,
                          height: 28,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: isActive ? 900 : 700,
                          color: isActive ? "#111827" : "#6B7280",
                          backgroundColor: isActive ? "#EEF2FF" : "transparent",
                          "&:hover": { backgroundColor: isActive ? "#EEF2FF" : "#F3F4F6" },
                        }}
                      >
                        {pageNumber}
                      </Button>
                    );
                  })}

                  <Button
                    disabled={loading || page >= pageCount - 1}
                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    sx={{
                      minWidth: 28,
                      height: 28,
                      borderRadius: 2,
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E5E7EB",
                      color: loading || page >= pageCount - 1 ? "#9CA3AF" : "#374151",
                      boxShadow: "none",
                      padding: 0,
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                        borderColor: "#D1D5DB",
                        boxShadow: "none",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#FFFFFF",
                        borderColor: "#E5E7EB",
                        color: "#9CA3AF",
                        opacity: 1,
                      },
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                  </Button>
                </Box>
              </Box>
            </Footer>
          </TablePane>

            {/* ================= RIGHT: GROUP SPLIT ================= */}
            {focusedGroup && (
              <Box
                sx={{
                  width: { xs: "100%", md: "40%" },
                  minWidth: { md: 340 },
                  flexShrink: 0,
                  borderLeft: { md: "1px solid rgba(0,0,0,0.10)" },
                  borderTop: { xs: "1px solid rgba(0,0,0,0.10)", md: 0 },
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  flexDirection: "column",
                  alignSelf: "stretch",
                  height: { xs: 420, md: "auto" },
                  overflow: "hidden",
                  position: { xs: "relative", md: "absolute" },
                  top: { md: 0 },
                  right: { md: 0 },
                  bottom: { md: 0 },
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 0,
                    height: 40,
                    minHeight: 40,
                    maxHeight: 40,
                    flex: "0 0 40px",
                    boxSizing: "border-box",
                    backgroundColor: "#F9FAFB",
                    borderBottom: "1px solid #E2E8F0",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      flexShrink: 0,
                      fontSize: 15,
                      lineHeight: 1,
                      fontWeight: 900,
                      color: "#111827",
                    }}
                  >
                    Group split
                  </Typography>
                  <Typography
                    title={focusedGroup.group}
                    sx={{
                      minWidth: 0,
                      fontSize: 12,
                      lineHeight: 1,
                      fontWeight: 600,
                      color: "#6B7280",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {focusedGroup.group}
                  </Typography>
                </Box>

                {focusedGroup.groupSplit.length > 0 ? (
                  <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <HeaderCell>Symbol/security</HeaderCell>
                          <HeaderCell>Markup</HeaderCell>
                          <HeaderCell>Rebate</HeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {focusedGroup.groupSplit.map((split, index) => (
                          <TableRow key={`${focusedGroup.id}-split-${index}`} hover>
                            <BodyCell>{split?.symbol_or_security ?? ""}</BodyCell>
                            <BodyCell>{split?.markup ?? ""}</BodyCell>
                            <BodyCell>{split?.rebate ?? ""}</BodyCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography sx={{ p: 2, fontSize: 13, color: "#6B7280" }}>
                    This group has no group split details.
                  </Typography>
                )}
              </Box>
            )}

            {/* ================= RIGHT: FILTERS PANEL ================= */}
            {filtersOpen && (
              <FiltersPane>
                <FiltersPaneHeader>
                  <Typography fontWeight={900} fontSize={22}>
                    Group filters
                  </Typography>
                  <IconButton onClick={closeFilters} size="small">
                    <CloseIcon />
                  </IconButton>
                </FiltersPaneHeader>

                <FiltersChipsRow>
                  {draftChips.map((c) => (
                    <Chip
                      key={c.key}
                      size="small"
                      label={c.label}
                      onDelete={() => removeDraftChip(c.key)}
                      sx={{
                        borderRadius: 999,
                        backgroundColor: "#FEEFC7",
                        color: "#5F4B00",
                        "& .MuiChip-deleteIcon": {
                          color: "#5F4B00",
                          "&:hover": { color: "#000" },
                        },
                      }}
                    />
                  ))}
                </FiltersChipsRow>

                <Divider />

                <FiltersForm>
                  <FormControl fullWidth size="small">
                    <InputLabel>Server</InputLabel>
                    <Select
                      label="Server"
                      value={draftFilters.server}
                      onChange={(e) =>
                        setDraftFilters((p) => ({ ...p, server: e.target.value }))
                      }
                    >
                      <MenuItem value="">All</MenuItem>
                      {serverOptions.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FiltersRow2>
                    <FormControl fullWidth size="small">
                      <InputLabel>Currency</InputLabel>
                      <Select
                        label="Currency"
                        value={draftFilters.currency}
                        onChange={(e) =>
                          setDraftFilters((p) => ({
                            ...p,
                            currency: e.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">All</MenuItem>
                        {currencyOptions.map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      size="small"
                      label={
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                          Markup <TableToolTip title="Markup value" />
                        </Box>
                      }
                      placeholder="Enter a markup"
                      value={draftFilters.markup}
                      onChange={(e) =>
                        setDraftFilters((p) => ({ ...p, markup: e.target.value }))
                      }
                      inputProps={{ inputMode: "numeric" }}
                    />
                  </FiltersRow2>

                  <FiltersRow2>
                    <TextField
                      fullWidth
                      size="small"
                      label={
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                          Rebate <TableToolTip title="Rebate value" />
                        </Box>
                      }
                      placeholder="Enter a value"
                      value={draftFilters.rebate}
                      onChange={(e) =>
                        setDraftFilters((p) => ({ ...p, rebate: e.target.value }))
                      }
                      inputProps={{ inputMode: "numeric" }}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      label={
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                          Symbol/Security <TableToolTip title="Symbol or security filter" />
                        </Box>
                      }
                      placeholder="Enter a symbol or security"
                      value={draftFilters.symbolSecurity}
                      onChange={(e) =>
                        setDraftFilters((p) => ({
                          ...p,
                          symbolSecurity: e.target.value,
                        }))
                      }
                    />
                  </FiltersRow2>
                </FiltersForm>

                <Divider />

                <FiltersActions>
                  <Button
                    variant="text"
                    onClick={clearFilters}
                    sx={{ textTransform: "none", fontWeight: 800 }}
                    startIcon={<IconComponent name="refresh" />}
                  >
                    Clear filters
                  </Button>

                  <Button
                    variant="contained"
                    onClick={applyFilters}
                    sx={{
                      textTransform: "none",
                      fontWeight: 900,
                      borderRadius: 2,
                      backgroundColor: "#EB0000",
                      "&:hover": {
                        backgroundColor: "#D50000",
                      },
                    }}
                  >
                    Apply filters
                  </Button>
                </FiltersActions>
              </FiltersPane>
            )}
          </CardSplit>
        </Box>
      </MainCard__body>
    </PageRoot>
  );
}

export default TradingGroups;
